import type { TranslateService } from '@ngx-translate/core';
import type { WebWidgetModel } from '@web/types';

import {
    EActionAfterConfirm,
    EButtonStyleType,
    EButtonWidthType,
    ECartIconType,
    ECartViewType,
    EFontStyle,
    EFormInputType,
    EHorizontalPosition,
    EHtmlAutocompleteAttr,
    EHtmlInputType,
    ELineStyle,
    ESize,
    FormStyleEnum,
    InputBorderEnum,
    Widgets,
} from '@common/enums';
import { defaultWidgetParams, defaultWidgetPermission } from '@common/constants';
import { defaultWidgetDataAdapter, isDefaultSuccessMessage } from '@common/helpers';

import {
    DEFAULT_BUTTON_ADDITIONAL_EFFECTS,
    DEFAULT_BUTTON_BASE,
    DEFAULT_BUTTON_HOVER_STATES,
    DEFAULT_BUTTON_ICON,
} from '@web/widgets/common/base/base-button/base-button.const';

import { ShopCartControlComponent, ShopCartViewComponent } from '@web/widgets/shop-cart/components';
import type { ShopCartWidgetData } from '@web/widgets/shop-cart/type';
import { ProductType, ProductUnits } from '@common/types/product-card.type';
import { EIconPosition } from '@common/enums/icon-position.enum';

export const defaultButtonValue = (label: string) => ({
    label,
    styleType: EButtonStyleType.primary,
    size: ESize.M,
    buttonBase: { ...DEFAULT_BUTTON_BASE },
    buttonHover: { ...DEFAULT_BUTTON_HOVER_STATES },
    widthType: EButtonWidthType.full,
    horizontalFormWidthType: EButtonWidthType.auto,
    widthValue: 260,
    buttonIcon: DEFAULT_BUTTON_ICON,
    additionalEffects: DEFAULT_BUTTON_ADDITIONAL_EFFECTS,
});

const shopCartData = (translateService: TranslateService): ShopCartWidgetData => ({
    createAddressBook: false,
    selectedAddressBook: null,
    selectedPipeline: null,
    selectedPipelineStep: null,
    selectedPipelineSettings: null,
    cartViewType: ECartViewType.HORIZONTAL,
    cartSettings: {
        previewSize: ESize.M,
        isSeparatedItems: true,
        separatorWidth: 1,
        separatorType: ELineStyle.SOLID,
        separatorColor: '#f3f3f3',
        itemTitleFontSize: 22,
        itemTitleFontStyle: [EFontStyle.B],
        itemTitleFontColor: '#333',
        itemPropertyFontSize: 14,
        itemPropertyFontStyle: [],
        itemPropertyFontColor: '#333',
        itemPriceFontSize: 18,
        itemPriceFontStyle: [],
        itemPriceFontColor: '#333',
        itemTotalPriceFontSize: 24,
        itemTotalPriceFontStyle: [],
        itemTotalPriceFontColor: '#333',
        deleteButtonIconColor: '#333',
        deleteButtonBgColor: '#f3f3f3',
        countButtonColor: '#f3f3f3',
        countButtonTextColor: '#333',
        countButtonBgColor: '#ffffff',
        countButtonBorderColor: '#d6e3e3',
        countButtonRounding: 4,
        countButtonSize: ESize.S,
    },
    cartIconSettings: {
        iconType: ECartIconType.ICON_1,
        iconBgColor: '#FFFFFF',
        iconColor: '#1D1C1C',
        iconCounterBgColor: '#FF2525',
        iconCounterColor: '#FFFFFF',
        iconRounding: 30,
        iconSize: ESize.M,
        iconPositionType: EIconPosition.SITE,
    },
    miniCartSettings: {
        modalBackgroundColor: '#ffffff',
        modalBorderRadius: 8,
        separatorColor: '#f3f3f3',
        textColor: '#1d1c1c',
        priceColor: '#1d1c1c',
        oldPriceColor: '#e87651',
        deleteButtonIconColor: '#1d1c1c',
        deleteButtonBgColor: '#f3f3f3',
        countButtonTextColor: '#1d1c1c',
        countButtonBgColor: '#ffffff',
        countButtonBorderColor: '#d6e3e3',
        countButtonRounding: 4,
        buttonBgColor: '#1d1c1c',
        buttonTextColor: '#ffffff',
        buttonBorderRadius: 4,
        buttonSettings: defaultButtonValue(translateService.instant('mini_cart_checkout_button')),
    },
    elements: [
        {
            type: EFormInputType.email,
            label: 'Email',
            placeholder: 'username@gmail.com',
            required: true,
            protected: true,
            isDynamicPlaceholderEnabled: false,
            crmContactVariable: null,
            crmDealVariable: null,
            variable: 'email',
            htmlInputType: EHtmlInputType.email,
            autocompleteType: EHtmlAutocompleteAttr.email,
        },
    ],
    paymentElements: [
        {
            type: 0,
            currency: 'USD',
            amount: 0,
            isEditableTotalCost: false,
            isActive: true,
            customPaymentName: translateService.instant('custom_payment_label'),
            newPaymentName: null,
        },
    ],
    dealName: translateService.instant('crm_deal_name'),
    paymentFormName: translateService.instant('shop_cart_payment_form'),
    createContactAndDeal: true,
    inputSettings: {
        modalBackgroundColor: '#eee',
        modalBorderRadius: 6,
        modalPadding: 30,
        showLabels: true,
        inputBorderType: InputBorderEnum.FULL_BORDER,
        formStyleType: FormStyleEnum.VERTICAL,
        backgroundColor: '#fff',
        borderColor: '#ddd',
        textColor: '#333',
        inputTextColor: '#333',
        borderRadius: 3,
        inputSize: ESize.M,
        horizontalPosition: EHorizontalPosition.L,
    },
    formButtonSettings: defaultButtonValue(translateService.instant('pay')),
    additionalFormSettings: {
        actionAfterConfirmType: EActionAfterConfirm.message,
        subscriptionMessage: `<h3>${translateService.instant('shop_cart_order_success_message')}</h3>`,
        updateExistingContact: false,
        openLink: null,
        promoCodeSettings: {
            isEnabled: false,
            label: translateService.instant('control_promocode_label_placeholder'),
            placeholder: translateService.instant('control_promocode_placeholder_placeholder'),
            selectedPromoCode: null,
        },
    },
    items: [
        {
            name: 'NordSeat',
            shortDescription: ``,
            description: ``,
            vendorCode: 'A453B43',
            quantity: 3,
            id: '1',
            unit: ProductUnits.pieces,
            price: 321,
            oldPrice: 4199,
            images: [
                {
                    url: './assets/img/websites/cart/empty-product-preview.png',
                    defaultCdnUrl: 'string',
                    path: '',
                    name: '',
                    size: 1,
                    filesCount: 1,
                    isFolder: false,
                },
            ],
            sections: [
                {
                    name: 'Характеристики',
                    parameters: [
                        {
                            parameter: 'Материал',
                            value: 'Хлопок',
                        },
                        {
                            parameter: 'Цвет',
                            value: 'Серый',
                        },
                    ],
                },
            ],
            variants: [],
            properties: [],
            type: ProductType.physical,
        },
        {
            name: 'NordSeat',
            shortDescription: ``,
            description: ``,
            vendorCode: 'A453B43',
            quantity: 2,
            id: '2',
            unit: ProductUnits.pieces,
            price: 15,
            oldPrice: 2699,
            images: [
                {
                    url: './assets/img/websites/cart/empty-product-preview.png',
                    defaultCdnUrl: 'string',
                    path: '',
                    name: '',
                    size: 1,
                    filesCount: 1,
                    isFolder: false,
                },
            ],
            sections: [
                {
                    name: 'Характеристики',
                    parameters: [
                        {
                            parameter: 'Материал',
                            value: 'Хлопок',
                        },
                        {
                            parameter: 'Цвет',
                            value: 'Серый',
                        },
                    ],
                },
            ],
            variants: [],
            properties: [],
            type: ProductType.physical,
        },
    ],
    analyticsSettings: {
        googleAnalytic: null,
        googleAnalyticAfterPayment: null,
        pixelAnalytic: null,
        pixelAnalyticAfterPayment: null,
        googleSettings: {
            category: null,
            action: null,
            label: null,
        },
        pixelSettings: {
            type: null,
            typeAfterPayment: null,
        },
    },
    addCrmTags: false,
    contactTags: [],
    addAddressBookTags: false,
    contactAddressBookTags: [],
});
// END DEFAULT SETTINGS

export const shopCartWidgetModel: WebWidgetModel = {
    componentView: ShopCartViewComponent,
    componentControl: ShopCartControlComponent,
    name: Widgets.SHOP_CART,
    label: 'widget_shop_cart_label',
    data: shopCartData,
    permission: defaultWidgetPermission,
    params: { ...defaultWidgetParams, hidden: true },
    dataAdapter: (data: any, modelData: ShopCartWidgetData) => {
        const adaptedData: ShopCartWidgetData = defaultWidgetDataAdapter(data, modelData);

        // the cart page comes from a template that keeps the success message as a locale-agnostic
        // token, so it is replaced with the default text in the current locale
        if (isDefaultSuccessMessage(adaptedData.additionalFormSettings?.subscriptionMessage)) {
            adaptedData.additionalFormSettings.subscriptionMessage =
                modelData.additionalFormSettings.subscriptionMessage;
        }

        return adaptedData;
    },
};
